import { motion } from "framer-motion";
import { fadeIn } from "@/lib/numa-assets";

const SectionLabel = ({ index, children }) => (
  <div className="flex items-center gap-4">
    <span className="numa-marker">{index}</span>
    <span className="h-[1px] w-12 bg-[#8A9A90]" />
    <span className="numa-marker text-[#152219]">{children}</span>
  </div>
);

export const ProductSystem = () => {
  return (
    <section
      id="protocol"
      data-testid="product-system-section"
      className="bg-[#F9F9F6] border-t border-[#E2E5E0] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div {...fadeIn} className="mb-16 sm:mb-24">
          <SectionLabel index="02">The NUMA Protocol</SectionLabel>
          <h2
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-[-0.025em] font-medium text-[#152219] mt-8 max-w-3xl leading-[1.04]"
            data-testid="product-system-title"
          >
            One protocol —
            <br />
            two precursor paths.
          </h2>
          <p className="text-[15px] leading-[1.7] text-[#57635A] mt-8 max-w-xl">
            NUMA offers a single architecture with a choice at its foundation:
            the NAD<sup>+</sup> precursor. Every other active in the protocol
            remains identical across both formulations.
          </p>
        </motion.div>

        {/* Two-variant diagram */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#E2E5E0] border border-[#E2E5E0]">
          {/* Variant A — NMN-05 */}
          <motion.div
            {...fadeIn}
            className="bg-white p-10 lg:p-14 lg:col-span-6 flex flex-col"
            data-testid="protocol-variant-nmn-05"
          >
            <div className="flex items-start justify-between mb-12">
              <span className="numa-marker">Variant A</span>
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#8A9A90]">
                NMN Path
              </span>
            </div>
            <h3 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#152219]">
              NMN-05
            </h3>
            <p className="text-[14px] leading-[1.7] text-[#57635A] mt-5 max-w-md">
              For those who want a precursor that sits one enzymatic step
              from NAD<sup>+</sup> itself — supported by recent human trials
              measuring muscle function, insulin sensitivity and blood NAD
              <sup>+</sup> elevation in adults over 45.
            </p>
            <div className="mt-auto pt-12 border-t border-[#E2E5E0] grid grid-cols-2 gap-6 text-[12px]">
              <div>
                <div className="text-[#8A9A90] text-[10px] tracking-[0.18em] uppercase">
                  Precursor
                </div>
                <div className="text-[#152219] mt-1">NMN — 500mg</div>
              </div>
              <div>
                <div className="text-[#8A9A90] text-[10px] tracking-[0.18em] uppercase">
                  Status
                </div>
                <div className="text-[#152219] mt-1">Waitlist Open</div>
              </div>
            </div>
          </motion.div>

          {/* Variant B — NR-05 */}
          <motion.div
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
            className="bg-[#3E5F4A] p-10 lg:p-14 lg:col-span-6 text-[#F9F9F6] relative flex flex-col"
            data-testid="protocol-variant-nr-05"
          >
            <div className="flex items-start justify-between mb-12">
              <span className="text-[11px] tracking-[0.22em] uppercase text-[#A8C0AF]">
                Variant B
              </span>
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A8C0AF]">
                NR Path
              </span>
            </div>
            <h3 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium">
              NR-05
            </h3>
            <p className="text-[14px] leading-[1.7] text-[#D6E1D9] mt-5 max-w-md">
              For those who want the precursor with the deepest human safety
              record — nearly a decade of published clinical trials, well-
              characterized pharmacokinetics and consistent blood NAD
              <sup>+</sup> elevation across broad adult populations.
            </p>
            <div className="mt-auto pt-12 border-t border-[#5B7A66] grid grid-cols-2 gap-6 text-[12px]">
              <div>
                <div className="text-[#A8C0AF] text-[10px] tracking-[0.18em] uppercase">
                  Precursor
                </div>
                <div className="mt-1">NR Chloride — 350mg</div>
              </div>
              <div>
                <div className="text-[#A8C0AF] text-[10px] tracking-[0.18em] uppercase">
                  Status
                </div>
                <div className="mt-1">Waitlist Open</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProductSystem;
